import { formatDateTime } from '~/lib/local-datetime';
import type { GitLocalVersion, GitNeedsAction } from './publish-stream';

/**
 * FRONT-3411: the one wording of a reconcile review, shared by the terminal
 * screen and the Save App dialog so the two can never disagree. A review is a
 * checkout review (local files or current app version) when the server sent
 * `localVersion` (src/pro-builder/git-lifecycle.ts); which side is older is
 * judged from content only, never from file or commit times.
 */
export type CheckoutSide = 'local' | 'current';

export interface ReconcileOption {
  choice: CheckoutSide;
  label: string;
  /** What pressing it does, in a sentence; null for a review without its own wording. */
  detail: string | null;
  /** Filled and focused: only when the content proves this is the side to take, or it is the only way on. */
  primary: boolean;
}

export interface ReconcileView {
  /** What the local files are, as a sentence; null when the review does not say. */
  history: string | null;
  options: ReconcileOption[];
}

const LOCAL_DETAIL = 'Starts from your local files. Where they differ, they replace the current app version in your draft the next time you save it.';
const CURRENT_DETAIL = 'Starts from the current app version. Your local files stay in a local recovery copy (Save App, Local recovery copies).';
const PURGED_LABEL = 'Open the current saved version';
const PURGED_DETAIL = 'Starts from the current saved version. Your local copy predates a change to the app’s history, so it is kept as a local recovery copy for reference and is not saved back.';

export function localVersionSentence(v: GitLocalVersion, format: (iso: string) => string = (iso) => formatDateTime(iso)): string {
  switch (v.kind) {
    case 'older-saved':
      return `Your local files match an older saved version of this app, from ${format(v.savedAt)}. The current app version was saved after it, so using your local files would put that older version back into your draft when you save it.`;
    case 'unsaved':
      return v.complete
        ? 'Your local files contain changes that were never saved to this app.'
        : `Your local files match none of this app’s last ${v.checked} saved versions, so they may contain changes that were never saved to it.`;
    case 'unknown':
      return 'EDDY could not check whether your local files match a saved version of this app.';
  }
}

/** The choice buttons a reconcile review offers, in order; empty when it offers none. */
export function reconcileView(action: GitNeedsAction, format?: (iso: string) => string): ReconcileView {
  if (!action.actions?.includes('reconcile')) return { history: null, options: [] };
  if (action.code === 'purged') return { history: null, options: [{ choice: 'current', label: PURGED_LABEL, detail: PURGED_DETAIL, primary: true }] };
  const v = action.localVersion;
  if (!v) return { history: null, options: [
    { choice: 'local', label: 'Reconcile using my local versions', detail: null, primary: false },
    { choice: 'current', label: 'Keep current app versions', detail: null, primary: false },
  ] };
  return { history: localVersionSentence(v, format), options: [
    { choice: 'local', label: 'Use my local files', detail: LOCAL_DETAIL, primary: false },
    { choice: 'current', label: 'Use the current app version', detail: CURRENT_DETAIL, primary: v.kind === 'older-saved' },
  ] };
}

/** The terminal screen's heading, lead sentence and file-list label for a reconcile review. */
export function terminalReviewCopy(action: GitNeedsAction): { title: string; lead: string; filesLabel: string } {
  if (action.code === 'purged') return { title: 'This app’s history changed', lead: action.message, filesLabel: 'Files removed from the app’s history' };
  if (action.localVersion) {
    const n = action.paths.length === 1 ? '1 file' : `${action.paths.length} files`;
    return { title: 'Choose which version this terminal starts from', lead: `Your local files for this app differ from its current version in ${n}.`, filesLabel: 'Files that differ' };
  }
  return { title: 'This app needs a decision before its terminal starts', lead: action.message, filesLabel: 'Files involved' };
}

/** The review a refused terminal open carries (`git` on its 409 body), or null for any other failure. */
export function terminalCheckoutReview(body: unknown): ({ folder: string } & GitNeedsAction) | null {
  if (!body || typeof body !== 'object') return null;
  const git = (body as { git?: unknown }).git;
  if (!git || typeof git !== 'object') return null;
  const g = git as Record<string, unknown>;
  if (typeof g.folder !== 'string' || typeof g.message !== 'string' || typeof g.code !== 'string' || !Array.isArray(g.paths)) return null;
  return git as { folder: string } & GitNeedsAction;
}
